import React, {
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import SendEmailTreeNode from '../../../../types/BotBuilder/SendEmailTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import SearchableDropdown, {
  DropdownItem
} from '../../../../components/SearchableDropdown/SearchableDropdown';
import { Label } from '../../../../components/ui/label';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor';
import PreviewModeContext from '../../../../context/PreviewModeContext/PreviewModeContext';
import { getSlotsByValueType } from '../../utils/intentutils';
import SlotDisplayConditionList from '../SlotDisplayConditionList/SlotDisplayConditionList';
import SlotDisplayCondition from '../../../../types/BotBuilder/SlotDisplayCondition';

export type SendEmailNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

const isSendEmailTreeNode = (content: any): content is SendEmailTreeNode => {
  return content instanceof SendEmailTreeNode;
};

function SendEmailNode(
  props: NodeProps<SendEmailNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeUpdate, onNodeDelete, intent, unconnectedNodes } =
    useContext(BotBuilderContext);
  const { isPreviewMode, setIsPreviewMode } = useContext(PreviewModeContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const sendEmailNode = isSendEmailTreeNode(data.node.content)
    ? data.node.content
    : null;

  const [recipient, setRecipient] = useState<string>(
    sendEmailNode?.recipient ?? ''
  );
  const [subject, setSubject] = useState<string>(sendEmailNode?.subject ?? '');
  const [content, setContent] = useState<string | null>(
    sendEmailNode?.content ?? null
  );
  const [attachmentVariableName, setAttachmentVariableName] = useState<string>(
    sendEmailNode?.attachmentVariableName ?? ''
  );
  const [slotDisplayConditions, setSlotDisplayConditions] = useState<
    SlotDisplayCondition[]
  >(sendEmailNode?.slotDisplayConditions ?? []);
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);

  const documentUploadSlots = useMemo(() => {
    return getSlotsByValueType(
      intent,
      unconnectedNodes,
      SlotValueType.DOCUMENT_UPLOAD
    );
  }, [intent, unconnectedNodes]);

  const emailSlots = useMemo(() => {
    return getSlotsByValueType(intent, unconnectedNodes, SlotValueType.EMAIL);
  }, [intent, unconnectedNodes]);

  const [emailSlotItems, setEmailSlotItems] = useState<DropdownItem[]>([]);
  const [emailSlotSearch, setEmailSlotSearch] = useState<string>('');

  useEffect(() => {
    const items = emailSlots.map((slot) => ({
      id: slot.slotName,
      label: slot.slotName,
      sublabel: 'Email variable'
    }));
    setEmailSlotItems(items);
  }, [emailSlots]);

  const filteredEmailSlotItems = useMemo(() => {
    if (!emailSlotSearch.trim()) return emailSlotItems;
    const searchLower = emailSlotSearch.toLowerCase();
    return emailSlotItems.filter(
      (item) =>
        item.label.toLowerCase().includes(searchLower) ||
        item.sublabel?.toLowerCase().includes(searchLower)
    );
  }, [emailSlotItems, emailSlotSearch]);

  const selectedRecipientItem = useMemo((): DropdownItem | null => {
    if (!recipient) return null;
    const matchingSlot = emailSlotItems.find(
      (item) => item.label === recipient
    );
    if (matchingSlot) return matchingSlot;
    return { id: recipient, label: recipient };
  }, [recipient, emailSlotItems]);

  const updateNodeData = useCallback(
    (updates: Partial<SendEmailTreeNode>) => {
      if (!sendEmailNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (sendEmailNode[key as keyof SendEmailTreeNode] !== value) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(sendEmailNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [sendEmailNode, onNodeUpdate, data.node]
  );

  const handleContentEditEnd = useCallback(() => {
    setIsEditingContent(false);
  }, []);

  const handleEditorPreview = useCallback(() => {
    handleContentEditEnd();
  }, [handleContentEditEnd]);

  useEffect(() => {
    if (sendEmailNode) {
      setRecipient(sendEmailNode.recipient);
      setSubject(sendEmailNode.subject);
      setContent(sendEmailNode.content);
      setAttachmentVariableName(sendEmailNode.attachmentVariableName ?? '');
    }
  }, [
    sendEmailNode?.recipient,
    sendEmailNode?.subject,
    sendEmailNode?.content,
    sendEmailNode?.attachmentVariableName
  ]);

  useEffect(() => {
    if (isPreviewMode && isEditingContent) {
      handleContentEditEnd();
    }
  }, [isPreviewMode, isEditingContent, handleContentEditEnd]);

  useDebouncedEffect(
    () => {
      if (sendEmailNode && recipient !== sendEmailNode.recipient) {
        updateNodeData({ recipient });
      }
    },
    [recipient, sendEmailNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (sendEmailNode && subject !== sendEmailNode.subject) {
        updateNodeData({ subject });
      }
    },
    [subject, sendEmailNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (sendEmailNode && content !== sendEmailNode.content) {
        updateNodeData({ content: content || '' });
      }
    },
    [content, sendEmailNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (
        sendEmailNode &&
        attachmentVariableName !== (sendEmailNode.attachmentVariableName ?? '')
      ) {
        updateNodeData({
          attachmentVariableName: attachmentVariableName || null
        });
      }
    },
    [attachmentVariableName, sendEmailNode, updateNodeData],
    300
  );

  const handleSlotDisplayConditionsChange = useCallback(
    (conditions: SlotDisplayCondition[]) => {
      setSlotDisplayConditions(conditions);
      updateNodeData({ slotDisplayConditions: conditions });
    },
    [updateNodeData]
  );

  const handleRecipientSelect = useCallback((item: DropdownItem) => {
    setRecipient(item.label);
  }, []);

  const handleRecipientCustomValue = useCallback((value: string) => {
    setRecipient(value);
  }, []);

  const handleRecipientSearch = useCallback((query: string) => {
    setEmailSlotSearch(query);
  }, []);

  const handleRecipientClear = useCallback(() => {
    setRecipient('');
  }, []);

  const handleSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSubject(value);
    },
    []
  );

  const handleContentEditStart = useCallback(
    (e: React.MouseEvent) => {
      if (data.isLocked) return;
      e.stopPropagation();
      setIsEditingContent(true);
      setIsPreviewMode(false);
    },
    [setIsPreviewMode, data.isLocked]
  );

  const handleContentChange = useCallback((updatedContent: string) => {
    setContent(updatedContent || null);
  }, []);

  const handleNodeClick = useCallback(
    (e: React.MouseEvent) => {
      if (isEditingContent) {
        const target = e.target as HTMLElement;
        const richTextEditor = target.closest('.rich-text-editor-wrapper');

        if (!richTextEditor) {
          handleContentEditEnd();
        }
      }
    },
    [isEditingContent, handleContentEditEnd]
  );

  const handleAttachmentVariableNameChange = useCallback((value: string) => {
    setAttachmentVariableName(value);
  }, []);

  return (
    <BotBuilderNode
      title='Send email'
      sourceNode={data.node}
      onNodeDelete={async () => {
        onNodeDelete(data.node);
      }}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -8 }}
        isConnectable={isConnectable}
        id='c'
      />

      <div
        role='button'
        tabIndex={0}
        aria-label='Email node configuration'
        className='flex flex-col gap-2 w-full bg-transparent p-0 text-left'
        onClick={handleNodeClick}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isEditingContent) {
            handleContentEditEnd();
          }
          const target = e.target as HTMLElement;
          const isInputElement =
            target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
          if ((e.key === 'Enter' || e.key === ' ') && !isInputElement) {
            e.preventDefault();
            handleNodeClick(e as any);
          }
        }}
      >
        <div className='flex flex-col gap-1'>
          <Label className='text-sm font-medium'>Recipient email</Label>
          <SearchableDropdown
            items={filteredEmailSlotItems}
            selectedItem={selectedRecipientItem}
            onSelect={handleRecipientSelect}
            onSearch={handleRecipientSearch}
            placeholder='Enter email or select variable'
            searchPlaceholder='Search email variables...'
            emptyText='No email variables found'
            allowCustomValue
            onCustomValueCreate={handleRecipientCustomValue}
            showClearButton
            onClear={handleRecipientClear}
            triggerClassName='bg-white rounded-lg'
            disabled={data.isLocked}
          />
        </div>

        <CustomInput
          label='Subject'
          value={subject}
          onChange={handleSubjectChange}
          placeholder='Enter email subject'
          className='bg-white'
          disabled={data.isLocked}
        />

        <div className='flex flex-col gap-2'>
          <Label className='text-sm font-medium'>Email content</Label>
          {!isPreviewMode && isEditingContent ? (
            <section aria-label='Email content editor'>
              <RichTextEditor
                textContent={content}
                onChange={handleContentChange}
                onPreview={handleEditorPreview}
                isEditable
                showToolbar
                placeholder='Enter email content'
                outputFormat='plaintext'
              />
            </section>
          ) : (
            <CustomTooltip content='Edit email content'>
              <button
                type='button'
                onClick={handleContentEditStart}
                className='cursor-pointer w-full border-none p-0 text-left bg-white text-sm rounded-lg'
              >
                {!content || content.trim() === '' ? (
                  <div className='px-3 py-2 text-sm text-muted-foreground'>
                    Enter email content
                  </div>
                ) : (
                  <RichTextEditor
                    textContent={content}
                    isEditable={false}
                    showToolbar={false}
                    className='preview-mode text-sm'
                    outputFormat='plaintext'
                  />
                )}
              </button>
            </CustomTooltip>
          )}
        </div>

        <SlotDisplayConditionList
          content={content ?? ''}
          slotDisplayConditions={slotDisplayConditions}
          onChange={handleSlotDisplayConditionsChange}
          disabled={data.isLocked}
        />

        <div className='mt-4'>
          <CustomSelect
            label='Document attachment (optional)'
            value={attachmentVariableName}
            onValueChange={handleAttachmentVariableNameChange}
            placeholder='Select document upload variable'
            options={documentUploadSlots.map((slot) => ({
              value: slot.slotName,
              label: slot.slotName
            }))}
            className='bg-white'
            disabled={data.isLocked}
          />
        </div>
      </div>

      <Handle
        type='source'
        position={Position.Bottom}
        style={{ bottom: -8 }}
        isConnectable={isConnectable}
        id='s'
      />
    </BotBuilderNode>
  );
}

export default SendEmailNode;
